import type { BaseDocument } from '../mongo/base-document.interface';
import type { WorkOrderDamageMark } from './work-order-damage';
import type { GarageKind } from '../supplier/supplier.interface';
import type { ServicePlanIntervalTrigger } from '../service-plan/service-plan.interface';
import type { ServicePlanReference } from '../service-reminder/service-reminder.interface';
import type { TerritoryCurrency } from '../territory/territory.interface';
import { VEHICLE_STATUS } from '../vehicle/vehicle.interface';
import type { VehicleStatus } from '../vehicle/vehicle.interface';

/**
 * The lifecycle status of a work order (SWOOP-325, D1). The full set is declared now so the stored
 * shape is lifecycle-ready, but SWOOP-325 only ever WRITES `OPEN` — every other transition belongs to
 * WOW 2 (SWOOP-327/328). `CLOSED` is the terminal "service done and signed off" state the maintenance
 * background reads from (D15); `CANCELLED` is a work order abandoned before completion.
 */
export const WORK_ORDER_STATUS = {
  AT_GARAGE: 'AT_GARAGE',
  CANCELLED: 'CANCELLED',
  CLOSED: 'CLOSED',
  DONE: 'DONE',
  OPEN: 'OPEN',
  RECEIVED: 'RECEIVED',
} as const;

export type WorkOrderStatus = (typeof WORK_ORDER_STATUS)[keyof typeof WORK_ORDER_STATUS];

/**
 * The statuses of a work order that is still running (SWOOP-336): opened and not yet closed or cancelled.
 * Used by the vehicle profile's Work orders card, its Maintenance badge and the Overview banner.
 */
export const WORK_ORDER_OPEN_STATUSES: readonly WorkOrderStatus[] = [
  WORK_ORDER_STATUS.OPEN,
  WORK_ORDER_STATUS.RECEIVED,
  WORK_ORDER_STATUS.AT_GARAGE,
  WORK_ORDER_STATUS.DONE,
];

/** Whether a work order with this status is still open (neither closed nor cancelled). */
export function isWorkOrderOpen(status: WorkOrderStatus): boolean {
  return WORK_ORDER_OPEN_STATUSES.includes(status);
}

/** Max length of a work order's free-text fields (notes, customer report, damage cause, item notes). */
export const WORK_ORDER_TEXT_MAX = 1000;

/** The `WorkOrder.source` values written by the system (SWOOP-304). */
export const WORK_ORDER_SOURCE = {
  REMINDER: 'reminder',
} as const;

/**
 * Whether the visit is planned maintenance or an unplanned fix (SWOOP-325, D7/D8). `SCHEDULED` copies
 * the operations of an interval of the vehicle's plan in force as its items; `NOT_SCHEDULED` requires a
 * reason instead ({@link WorkOrderNotScheduledReason}).
 */
export const WORK_ORDER_TYPE = {
  NOT_SCHEDULED: 'NOT_SCHEDULED',
  SCHEDULED: 'SCHEDULED',
} as const;

export type WorkOrderType = (typeof WORK_ORDER_TYPE)[keyof typeof WORK_ORDER_TYPE];

/**
 * Why an unplanned (`NOT_SCHEDULED`) work order was opened (SWOOP-325, D8). The same list as the item
 * reasons ({@link WorkOrderItemReason}). `DAMAGE` additionally requires the {@link WorkOrderDamage} block.
 */
export const WORK_ORDER_NOT_SCHEDULED_REASON = {
  CORRECTIVE: 'CORRECTIVE',
  DAMAGE: 'DAMAGE',
  PREVENTIVE: 'PREVENTIVE',
} as const;

export type WorkOrderNotScheduledReason =
  (typeof WORK_ORDER_NOT_SCHEDULED_REASON)[keyof typeof WORK_ORDER_NOT_SCHEDULED_REASON];

/**
 * Why a single item to fix is on the work order (SWOOP-325, D10). `PREVENTIVE` is a planned-maintenance
 * operation (every item copied from a scheduled interval is preventive); `CORRECTIVE` repairs a fault;
 * `DAMAGE` fixes accident damage.
 */
export const WORK_ORDER_ITEM_REASON = {
  CORRECTIVE: 'CORRECTIVE',
  DAMAGE: 'DAMAGE',
  PREVENTIVE: 'PREVENTIVE',
} as const;

export type WorkOrderItemReason = (typeof WORK_ORDER_ITEM_REASON)[keyof typeof WORK_ORDER_ITEM_REASON];

/**
 * Where an item to fix came from (SWOOP-325). `interval` items are copied from the scheduled plan
 * interval's operations; `additional_point` items are the free rows the user adds in the drawer.
 */
export const WORK_ORDER_ITEM_SOURCE = {
  ADDITIONAL_POINT: 'additional_point',
  INTERVAL: 'interval',
} as const;

export type WorkOrderItemSource = (typeof WORK_ORDER_ITEM_SOURCE)[keyof typeof WORK_ORDER_ITEM_SOURCE];

/**
 * The lifecycle events recorded on a work order's {@link WorkOrder.timeline} (SWOOP-250, WOW 2). One
 * entry is appended per transition, append-only: `OPENED` when the work order is created, then one of
 * `RECEIVED` / `SENT_TO_GARAGE` / `MARKED_DONE` / `CLOSED` / `CANCELLED` as it advances. The set is
 * declared in full now so the timeline is lifecycle-ready, but this slice only WRITES `OPENED`,
 * `RECEIVED` and `SENT_TO_GARAGE` — the rest arrive with mark-done / close / cancel.
 */
export const WORK_ORDER_TIMELINE_EVENT = {
  CANCELLED: 'CANCELLED',
  CLOSED: 'CLOSED',
  /** A document was deleted from the work order (SWOOP-335, doc Q15). Appended without a status change. */
  DOCUMENT_DELETED: 'DOCUMENT_DELETED',
  /** A document was uploaded to the work order (SWOOP-254). Appended without a status change. */
  DOCUMENT_UPLOADED: 'DOCUMENT_UPLOADED',
  MARKED_DONE: 'MARKED_DONE',
  OPENED: 'OPENED',
  RECEIVED: 'RECEIVED',
  SENT_TO_GARAGE: 'SENT_TO_GARAGE',
} as const;

export type WorkOrderTimelineEvent = (typeof WORK_ORDER_TIMELINE_EVENT)[keyof typeof WORK_ORDER_TIMELINE_EVENT];

/** Who is responsible for accident damage (SWOOP-325, D9). */
export const DAMAGE_RESPONSIBILITY = {
  CUSTOMER: 'CUSTOMER',
  OUR_DRIVER: 'OUR_DRIVER',
  THIRD_PARTY: 'THIRD_PARTY',
  UNKNOWN: 'UNKNOWN',
} as const;

export type DamageResponsibility = (typeof DAMAGE_RESPONSIBILITY)[keyof typeof DAMAGE_RESPONSIBILITY];

/** The nature of accident damage (SWOOP-325, D9). */
export const DAMAGE_NATURE = {
  BODY: 'BODY',
  MECHANICAL: 'MECHANICAL',
} as const;

export type DamageNature = (typeof DAMAGE_NATURE)[keyof typeof DAMAGE_NATURE];

/**
 * The static list of vehicle sections an item to fix can be tagged with (SWOOP-325, D11). Stored as the
 * key (e.g. `engine`); the label is translated in the UI. A new section is added here and translated,
 * never stored as free text.
 */
export const VEHICLE_SECTION = {
  AIR_CONDITIONING: 'air_conditioning',
  BODY: 'body',
  BRAKES: 'brakes',
  ELECTRICAL: 'electrical',
  ENGINE: 'engine',
  EXHAUST: 'exhaust',
  INTERIOR: 'interior',
  OTHER: 'other',
  SUSPENSION_STEERING: 'suspension_steering',
  TRANSMISSION: 'transmission',
  TYRES_WHEELS: 'tyres_wheels',
} as const;

export type VehicleSection = (typeof VEHICLE_SECTION)[keyof typeof VEHICLE_SECTION];

export const VEHICLE_SECTIONS = Object.values(VEHICLE_SECTION);

/**
 * One item to fix on a work order (SWOOP-325, D7/D10). `label` carries the operation name (for a copied
 * interval operation) or the free-text point; `serviceOperationId` is set only for an interval item.
 * `section` is the optional {@link VehicleSection} tag (never set on an interval item — D7).
 */
export interface WorkOrderItem {
  label: string;
  reason: WorkOrderItemReason;
  /** Optional vehicle section (D11); absent on copied interval items (D7). */
  section?: VehicleSection | null;
  /** `service_operations` row id (ObjectId, hex on the wire) for a copied interval operation; absent otherwise. */
  serviceOperationId?: string | null;
  source: WorkOrderItemSource;
  /**
   * When the item was verified during per-item verification (SWOOP-250, WOW 2). Declared now; written by
   * the item-verification step (not this slice). `null`/absent until the item is verified.
   */
  verifiedAt?: Date | null;
  /** The user (`users` row id, ObjectId hex) who verified the item; `null`/absent until verified (SWOOP-250). */
  verifiedBy?: string | null;
}

/**
 * The accident-damage block of a `DAMAGE` work order (SWOOP-325, D9): the nature of the damage, the
 * free-text cause of the accident and who is responsible.
 *
 * `responsibility` is FREE TEXT since SWOOP-335 (doc Q8, R16): the person responsible, trimmed, 1–200 chars.
 * No migration — a work order saved before the change carries one of the four legacy
 * {@link DAMAGE_RESPONSIBILITY} codes (`CUSTOMER` / `OUR_DRIVER` / `THIRD_PARTY` / `UNKNOWN`), which the UI
 * still displays as its translated label; anything typed since is shown as-is. The `DAMAGE_RESPONSIBILITY`
 * const is kept only for that legacy display mapping.
 */
export interface WorkOrderDamage {
  /** Free-text cause of the accident. */
  cause: string;
  nature: DamageNature;
  /** Free text (1–200): the person responsible. Legacy values are one of the {@link DAMAGE_RESPONSIBILITY} codes. */
  responsibility: string;
}

/**
 * The payload recorded on a `SENT_TO_GARAGE` timeline entry (SWOOP-250, WOW 2): the garage the vehicle
 * was sent to, the odometer read on the way out, the (optional) expected completion date and the
 * (optional) additional instructions — all a snapshot of the send-to-garage action as it happened.
 */
export interface WorkOrderTimelineSentToGarageDetails {
  expectedCompletionDate: Date | null;
  /** The chosen garage (`suppliers` row id, ObjectId hex). */
  garageId: string;
  instructions: string | null;
  /** The odometer read as the vehicle left for the garage (km). */
  odometerOut: number;
}

/**
 * The payload recorded on a `CLOSED` timeline entry (SWOOP-250, WOW 2): a snapshot of the invoice and
 * odometer captured when the work order was closed — the invoice reference, its net / VAT amounts and the
 * currency they were recorded in, the linked expense, and the odometer read on the way back in.
 */
export interface WorkOrderClosedTimelineDetails {
  /** The currency the invoice amounts were recorded in (snapshotted from the vehicle's territory). */
  currency: TerritoryCurrency;
  /** The expense the close created (`expenses` row id, ObjectId hex). */
  expenseId: string;
  invoiceReference: string;
  netAmount: number;
  /** The odometer read when the vehicle came back in (km). */
  odometerIn: number;
  vatAmount: number | null;
}

/**
 * The payload recorded on a `DOCUMENT_UPLOADED` timeline entry (SWOOP-254): which document was uploaded
 * and its file name, so the Timeline tab can name the file without resolving the document row.
 */
export interface WorkOrderDocumentUploadedTimelineDetails {
  /** The uploaded document (`documents` row id, ObjectId hex). */
  documentId: string;
  /** The uploaded file's original name, snapshotted so the entry reads even if the document is later deleted. */
  fileName: string;
}

/**
 * The payload recorded on a `DOCUMENT_DELETED` timeline entry (SWOOP-335, doc Q15): which document was
 * deleted and its file name, so the Timeline tab can name the file even though the document row is gone.
 * Same shape as {@link WorkOrderDocumentUploadedTimelineDetails}, kept distinct so the entry types stay
 * discriminated.
 */
export interface WorkOrderDocumentDeletedTimelineDetails {
  /** The deleted document (`documents` row id, ObjectId hex). */
  documentId: string;
  /** The deleted file's original name, snapshotted so the entry reads after the document is gone. */
  fileName: string;
}

/**
 * The payload an `OPENED` timeline entry carries when the work order was opened FROM an issue (SWOOP-335,
 * doc Q16/Q17, R17): the issue number and how many of its photos were copied, so the entry reads "Opened
 * from ISS-n · N photos copied". `null` (no details) on a work order opened directly. No separate
 * linked / created / unlinked entry is written (doc Q17).
 */
export interface WorkOrderOpenedTimelineDetails {
  /** How many of the issue's photos were copied onto the work order's documents (≥ 0). */
  photosCopied: number;
  /** The human issue number of the source issue (e.g. `42` for `ISS-42`). */
  sourceIssueNumber: number;
}

/**
 * The typed `details` payloads a timeline entry can carry (SWOOP-250/254/335): opened-from-issue,
 * send-to-garage, close, document upload, document delete.
 */
export type WorkOrderTimelineDetails =
  | WorkOrderClosedTimelineDetails
  | WorkOrderDocumentDeletedTimelineDetails
  | WorkOrderDocumentUploadedTimelineDetails
  | WorkOrderOpenedTimelineDetails
  | WorkOrderTimelineSentToGarageDetails;

/** Fields common to every {@link WorkOrderTimelineEntry}: when it happened and the acting user. */
interface WorkOrderTimelineEntryBase {
  /** When the event happened (server-stamped). */
  at: Date;
  /** The acting user (`users` row id, ObjectId hex). */
  by: string;
}

/**
 * `OPENED` timeline entry (SWOOP-250): the work order was created. `details` is `null` for a work order
 * opened directly, or {@link WorkOrderOpenedTimelineDetails} when it was opened from an issue (SWOOP-335,
 * R17) — carrying the source issue number and how many of its photos were copied.
 */
export interface WorkOrderOpenedTimelineEntry extends WorkOrderTimelineEntryBase {
  details: WorkOrderOpenedTimelineDetails | null;
  type: typeof WORK_ORDER_TIMELINE_EVENT.OPENED;
}

/** `RECEIVED` timeline entry (SWOOP-250, W3): the vehicle was received. No payload. */
export interface WorkOrderReceivedTimelineEntry extends WorkOrderTimelineEntryBase {
  details: null;
  type: typeof WORK_ORDER_TIMELINE_EVENT.RECEIVED;
}

/** `SENT_TO_GARAGE` timeline entry (SWOOP-250, W5): the vehicle was sent to the garage. */
export interface WorkOrderSentToGarageTimelineEntry extends WorkOrderTimelineEntryBase {
  details: WorkOrderTimelineSentToGarageDetails;
  type: typeof WORK_ORDER_TIMELINE_EVENT.SENT_TO_GARAGE;
}

/** `MARKED_DONE` timeline entry (SWOOP-250). Declared now; written by the mark-done step (not this slice). */
export interface WorkOrderMarkedDoneTimelineEntry extends WorkOrderTimelineEntryBase {
  details: null;
  type: typeof WORK_ORDER_TIMELINE_EVENT.MARKED_DONE;
}

/** `CLOSED` timeline entry (SWOOP-250, WOW 2): the work order was closed, carrying the invoice snapshot. */
export interface WorkOrderClosedTimelineEntry extends WorkOrderTimelineEntryBase {
  details: WorkOrderClosedTimelineDetails;
  type: typeof WORK_ORDER_TIMELINE_EVENT.CLOSED;
}

/** `CANCELLED` timeline entry (SWOOP-250). Declared now; written by the cancel step (not this slice). */
export interface WorkOrderCancelledTimelineEntry extends WorkOrderTimelineEntryBase {
  details: null;
  type: typeof WORK_ORDER_TIMELINE_EVENT.CANCELLED;
}

/** `DOCUMENT_UPLOADED` timeline entry (SWOOP-254): a document was uploaded to the work order. */
export interface WorkOrderDocumentUploadedTimelineEntry extends WorkOrderTimelineEntryBase {
  details: WorkOrderDocumentUploadedTimelineDetails;
  type: typeof WORK_ORDER_TIMELINE_EVENT.DOCUMENT_UPLOADED;
}

/** `DOCUMENT_DELETED` timeline entry (SWOOP-335, doc Q15): a document was deleted from the work order. */
export interface WorkOrderDocumentDeletedTimelineEntry extends WorkOrderTimelineEntryBase {
  details: WorkOrderDocumentDeletedTimelineDetails;
  type: typeof WORK_ORDER_TIMELINE_EVENT.DOCUMENT_DELETED;
}

/**
 * One append-only entry on a work order's lifecycle timeline (SWOOP-250, WOW 2): a discriminated union
 * on `type`, so the `details` payload is typed per event. Entries are stored oldest-first (append order);
 * the detail read returns them newest-first.
 */
export type WorkOrderTimelineEntry =
  | WorkOrderOpenedTimelineEntry
  | WorkOrderReceivedTimelineEntry
  | WorkOrderSentToGarageTimelineEntry
  | WorkOrderMarkedDoneTimelineEntry
  | WorkOrderClosedTimelineEntry
  | WorkOrderCancelledTimelineEntry
  | WorkOrderDocumentUploadedTimelineEntry
  | WorkOrderDocumentDeletedTimelineEntry;

/**
 * The supplier invoice captured when a work order is closed (SWOOP-250, WOW 2). Declared now; written by
 * the close step (not this slice). `expenseId` links the expense the close creates (`expenses` row id).
 */
export interface WorkOrderInvoice {
  date: Date;
  /** The linked expense (`expenses` row id, ObjectId hex). */
  expenseId: string;
  netAmount: number;
  reference: string;
  vatAmount?: number | null;
}

/**
 * A work order: the record of one vehicle visit to a garage (SWOOP-325). Created in `OPEN`, which takes
 * the vehicle out of service (`UNDER_MAINTENANCE`) and records its odometer. Soft-deletable via
 * `deletedAt` (no delete route in SWOOP-325).
 *
 * `locationId` is copied from the vehicle at creation time. Like a vehicle, a work order has no direct
 * territoryId: its territory is that of its location, so a territory selection is resolved to its locations
 * and matched on `locationId`, exactly as the vehicle list does.
 */
export interface WorkOrder extends BaseDocument {
  /** Reported by the customer, optional free text (D12). */
  customerReport?: string | null;
  /** The accident-damage block — present only when a `NOT_SCHEDULED` reason is `DAMAGE` (D9). */
  damage?: WorkOrderDamage | null;
  /**
   * The damage-chart marks (SWOOP-335): a `DAMAGE` work order's numbered damages, embedded. Absent (never
   * present) on every work order opened before SWOOP-335 and on every non-DAMAGE work order — no migration.
   */
  damages?: WorkOrderDamageMark[];
  /**
   * The last damage number handed out (SWOOP-335): `$inc`-ed on each add, never decremented, so a deleted
   * number is never reused (doc Q32). Absent until the first damage is added.
   */
  damageSequence?: number;
  /** Optional expected completion date (D13). */
  expectedCompletionDate?: Date | null;
  /** The chosen garage (`suppliers` row id, ObjectId hex) — optional (D13). */
  garageId?: string | null;
  /** The interval code of the scheduled plan interval (SCHEDULED only, D7). */
  intervalCode?: string | null;
  /**
   * The supplier invoice captured at close (SWOOP-250, WOW 2), written by the close step. `null`/absent
   * until closed.
   */
  invoice?: WorkOrderInvoice | null;
  /**
   * The issue this work order is bound to, one-to-one (SWOOP-248, WOW 1 US-8; `issues` row id, ObjectId
   * hex). Set only when a NOT_SCHEDULED work order is opened from the Work orders screen, which auto-creates
   * an issue in the same transaction; `null` for SCHEDULED work orders and for any opened before US-8.
   */
  issueId?: string | null;
  /** Display copy of the bound issue's number, e.g. `ISS-42` (SWOOP-248, WOW 1 US-8); `null` when none. */
  issueNumber?: string | null;
  /** The items to fix: copied interval operations and/or additional points (D7/D10). */
  items: WorkOrderItem[];
  /** The vehicle's location at creation time. */
  locationId: string;
  /** The "motives", required free text (D10). */
  notes: string;
  /**
   * Odometer recorded at opening when one was given — also written as an `ODOMETER_READING` history
   * entry (D5). `null` when the opener left it blank: no reading is written and reminders are not touched.
   */
  odometer: number | null;
  /**
   * The odometer read when the vehicle came back from the garage (km) — SWOOP-250, WOW 2. Declared now;
   * written by the mark-done / return step (not this slice). `null`/absent until then.
   */
  odometerIn?: number | null;
  /**
   * The odometer read as the vehicle left for the garage (km) — SWOOP-250, W5. Written by send-to-garage,
   * where it is also recorded as an `ODOMETER_READING` history entry. `null`/absent until the work order
   * is sent to a garage.
   */
  odometerOut?: number | null;
  /** The acting user's id. */
  openedBy: string;
  /**
   * Additional free-text instructions given when the vehicle was sent to the garage (SWOOP-250, W5,
   * "Additional instructions"). `null`/absent when none were given (or before send-to-garage).
   */
  instructions?: string | null;
  /** Sequential per-tenant human reference, e.g. `WO-000123` (D2). */
  number: string;
  /** The `service_plans` row id of the plan in force (SCHEDULED only, D7; ObjectId hex). */
  planId?: string | null;
  /** The unplanned reason (NOT_SCHEDULED only, D8). */
  reason?: WorkOrderNotScheduledReason | null;
  /**
   * Where the work order came from (SWOOP-325, D17). `'reminder'` ({@link WORK_ORDER_SOURCE}) when opened from
   * a service reminder (SWOOP-304); `''` otherwise — an issue-origin work order is recognised by `issueId`
   * (SWOOP-305). Never client-supplied.
   */
  source: string;
  /**
   * When the vehicle came back from the garage (SWOOP-250, WOW 2). Declared now; written by the
   * mark-done / return step (not this slice). `null`/absent until then.
   */
  returnedOn?: Date | null;
  status: WorkOrderStatus;
  /**
   * The lifecycle timeline (SWOOP-250, WOW 2): append-only, oldest-first. One entry per transition.
   * Optional so work orders opened before WOW 2 (no entries) still read fine — the detail derives a
   * synthetic `OPENED` entry for them via {@link deriveWorkOrderTimeline}.
   */
  timeline?: WorkOrderTimelineEntry[];
  type: WorkOrderType;
  /** Whether the work is under the vehicle's warranty (D14). */
  underWarranty: boolean;
  /** The `vehicles` row id (ObjectId hex). */
  vehicleId: string;
  /**
   * The vehicle's lifecycle status (ACTIVE / INACTIVE) at the moment the work order was opened
   * (SWOOP-250, WOW 2). Optional: work orders opened before WOW 2 have none, and readers must treat a
   * missing value as ACTIVE via {@link resolveVehicleStatusAtOpening}.
   */
  vehicleStatusAtOpening?: VehicleStatus | null;
  /**
   * The free-text note captured during verification (SWOOP-250, WOW 2). Declared now; written by the
   * verification step (not this slice). `null`/absent until then.
   */
  verificationNote?: string | null;
  /**
   * The work-order date: set by the server to the moment the work order is opened (D6). Never
   * client-supplied — a sent date is rejected; the form shows today read-only.
   */
  workOrderDate: Date;
}

/**
 * The vehicle status recorded at opening, treating a missing value as `ACTIVE` (SWOOP-250, WOW 2). Work
 * orders opened before WOW 2 have no `vehicleStatusAtOpening`; this single helper is the one place that
 * fallback lives, so every reader agrees.
 */
export function resolveVehicleStatusAtOpening(workOrder: Pick<WorkOrder, 'vehicleStatusAtOpening'>): VehicleStatus {
  return workOrder.vehicleStatusAtOpening ?? VEHICLE_STATUS.ACTIVE;
}

/**
 * The work order's lifecycle timeline, oldest-first (SWOOP-250, WOW 2). A work order opened before WOW 2
 * never stored an `OPENED` entry; this derivation PREPENDS a synthetic one (from `createdAt` + `openedBy`)
 * whenever the stored timeline has no `OPENED` entry — so a legacy work order keeps its opening event even
 * after a later transition (e.g. `RECEIVED`) is appended, not only while the timeline is still empty. The
 * synthetic entry is NOT persisted, only surfaced on read. The one place this derivation lives, so every
 * reader agrees.
 */
export function deriveWorkOrderTimeline(
  workOrder: Pick<WorkOrder, 'createdAt' | 'openedBy' | 'timeline'>,
): WorkOrderTimelineEntry[] {
  const stored = workOrder.timeline ?? [];
  if (stored.some((entry) => entry.type === WORK_ORDER_TIMELINE_EVENT.OPENED)) {
    return stored;
  }
  const opened: WorkOrderOpenedTimelineEntry = {
    at: workOrder.createdAt,
    by: workOrder.openedBy,
    details: null,
    type: WORK_ORDER_TIMELINE_EVENT.OPENED,
  };
  return [opened, ...stored];
}

/** One additional point the drawer sends (SWOOP-325, D10). Empty rows are dropped server-side. */
export interface CreateWorkOrderAdditionalPoint {
  label?: string;
  reason?: WorkOrderItemReason;
  section?: VehicleSection | null;
}

/**
 * What the drawer POSTs to open a work order (SWOOP-325). System-managed fields (`number`, `status`,
 * `items`, `source`, `openedBy`, `locationId`, `workOrderDate`) are never client-supplied —
 * they are derived in the service; the date is the moment the work order is opened (D6). `underWarranty`
 * defaults to `workOrderDate < warrantyExpiryDate` when omitted (D14); `odometer` is optional (D5).
 */
export interface CreateWorkOrder {
  additionalPoints?: CreateWorkOrderAdditionalPoint[];
  customerReport?: string | null;
  damage?: WorkOrderDamage | null;
  expectedCompletionDate?: Date | null;
  garageId?: string | null;
  intervalCode?: string | null;
  /**
   * The existing issue this work order is opened from (SWOOP-305). When present the work order links that
   * issue instead of auto-creating one; it must be NOT_SCHEDULED with the reason mapped from the issue type.
   */
  issueId?: string;
  notes: string;
  /** Odometer at opening (D5); optional. When present it becomes a measured reading and reminders recalculate. */
  odometer?: number | null;
  planId?: string | null;
  reason?: WorkOrderNotScheduledReason | null;
  /**
   * The service reminder this work order is opened from (SWOOP-304). When present the work order must be
   * SCHEDULED for that reminder's plan and interval, the odometer is required, and the reminder is linked to
   * the new work order in the same transaction. Never together with `issueId`.
   */
  serviceReminderId?: string;
  type: WorkOrderType;
  underWarranty?: boolean;
  vehicleId: string;
}

/**
 * The domain inputs of `POST /work-orders/:id/close` (SWOOP-250, WOW 2). Closes a `DONE` work order:
 * records the odometer read on the way back in, captures the supplier invoice (which the close turns into
 * a vehicle expense) and an optional verification note. `vatAmount` is genuinely optional (absent when the
 * invoice carries no VAT); `verificationNote` empty collapses to absent in the service.
 */
export interface CloseWorkOrder {
  /** The invoice date; required, not in the future. */
  invoiceDate: Date;
  /** The supplier invoice reference; required, non-empty. */
  invoiceReference: string;
  /** The invoice net amount; required, > 0, at most 2 decimals. */
  netAmount: number;
  /** The odometer read when the vehicle came back in (km); required, ≥ `odometerOut` and ≥ the latest reading. */
  odometerIn: number;
  /** When the vehicle came back; required, not before the day it was sent to the garage, not in the future. */
  returnedOn: Date;
  /** The invoice VAT amount; optional, ≥ 0, at most 2 decimals. */
  vatAmount?: number | null;
  /** Optional verification note. */
  verificationNote?: string | null;
}

/**
 * One interval of the vehicle's plan in force, with its next due point (SWOOP-325, D7). Feeds the
 * drawer's Scheduled interval picker (`A (15,000 km — 12 Sep 2026)`). The due point is read exactly
 * as the service-reminder view computes it, so the drawer and the reminders page agree.
 */
export interface WorkOrderIntervalOption {
  code: string;
  dueDate: Date | null;
  dueOdometer: number | null;
  mileageKm: number | null;
  months: number | null;
  name: string;
  projectedDate: Date | null;
  trigger: ServicePlanIntervalTrigger;
}

/** One garage the drawer can send the vehicle to (SWOOP-325, D13). */
export interface WorkOrderGarageOption {
  garageKind: GarageKind;
  id: string;
  legalName: string;
  tradeName?: string | null;
}

/**
 * The read-only maintenance background the Open-work-order drawer shows once a vehicle is picked
 * (SWOOP-325, D13/D15). Served by `GET /vehicles/:vehicleId/work-order-context`.
 *
 * `lastService*` / `lastRepair*` come from the last `CLOSED` SCHEDULED / NOT_SCHEDULED work order and
 * are `null` until closing exists (WOW 2, D15). `intervals` is the plan in force's intervals with their
 * due points (empty when no plan is in force — Scheduled is then unavailable, D7). `garages` are the
 * ACTIVE GARAGE suppliers of the vehicle's territory, split by `garageKind`.
 */
export interface VehicleMaintenanceBackground {
  garages: { outside: WorkOrderGarageOption[]; own: WorkOrderGarageOption[] };
  intervals: WorkOrderIntervalOption[];
  lastRepairDate: Date | null;
  lastRepairOdometer: number | null;
  lastServiceDate: Date | null;
  lastServiceOdometer: number | null;
  latestOdometer: number | null;
  /** When the latest odometer reading was taken, or null when the vehicle has none. */
  latestOdometerDate: Date | null;
  planInForce: ServicePlanReference | null;
  /** Whether Under warranty would be auto-ticked for a work order dated today (D14). */
  underWarrantyByDefault: boolean;
  warrantyExpiryDate: Date | null;
}
